import { useCallback, useEffect, useState } from 'react';
import type { FundView } from '../lib/reports';
import { reportsApi } from '../lib/reports';
import { ApiError } from '../lib/api';
import { openPrint } from '../lib/print';
import { formatDate, formatRs } from '../lib/money';
import { ContributionSchedule, FundStatement } from '../print/FundDocuments';

/**
 * EPF and ETF — the fund card. Owner only.
 *
 * "An outstanding-by-member table (name, EPF number, base salary, EPF, ETF),
 * Pay / Skip per member with select all and clear so a partial payment can be
 * made, and recording a payment takes only the month, the date paid and a
 * reference — the amounts come from the calculation, never typed."
 *
 * There is deliberately no amount field on this screen.
 */

export default function Funds({ userName, today }: { userName: string; today: string }) {
  const [view, setView] = useState<FundView | null>(null);
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [month, setMonth] = useState(today.slice(0, 7));
  const [paidOn, setPaidOn] = useState(today);
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [voiding, setVoiding] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await reportsApi.funds());
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the fund figures.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (!view) return <div className="empty">{error || 'Loading…'}</div>;

  const picked = view.outstanding.filter((row) => !skipped.has(row.worker_id));
  const pickedTotals = picked.reduce(
    (acc, row) => ({
      epf: acc.epf + row.epf_employee_rs + row.epf_employer_rs,
      etf: acc.etf + row.etf_rs,
    }),
    { epf: 0, etf: 0 },
  );
  const owed = view.totals.epfOwedRs + view.totals.etfOwedRs;
  const rates = view.rates;

  async function record() {
    setBusy(true);
    setError('');
    try {
      await reportsApi.recordPayment({
        periodMonth: month,
        paidOn,
        reference: reference.trim() || null,
        workerIds: picked.map((row) => row.worker_id),
      });
      setSkipped(new Set());
      setReference('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record that payment.');
    } finally {
      setBusy(false);
    }
  }

  async function printSchedule(paymentId: string) {
    try {
      const schedule = await reportsApi.schedule(paymentId);
      openPrint(
        `EPF/ETF schedule — ${schedule.payment.period_month}`,
        <ContributionSchedule schedule={schedule} printedBy={userName} printedAt={new Date()} />,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open that schedule.');
    }
  }

  return (
    <div className="card">
      <h3>EPF and ETF</h3>
      <div className="sub">
        On base salary only, from approved pay runs.
        {rates && (
          <>
            {' '}EPF {rates.epf_employee_pct_x100 / 100}% employee +{' '}
            {rates.epf_employer_pct_x100 / 100}% employer, ETF {rates.etf_pct_x100 / 100}%
            employer.
          </>
        )}{' '}
        Paid to the fund member by member.
      </div>

      {error && <div className="banner b-no">{error}</div>}

      <div className="grid2" style={{ marginTop: 10 }}>
        <div className="metric">
          <div className="k">EPF due to date</div>
          <div className="v">{formatRs(view.totals.epfDueRs)}</div>
          <div className="k">
            {formatRs(view.totals.epfEmployeeRs)} employee +{' '}
            {formatRs(view.totals.epfEmployerRs)} employer
          </div>
        </div>
        <div className="metric">
          <div className="k">ETF due to date</div>
          <div className="v">{formatRs(view.totals.etfDueRs)}</div>
          <div className="k">employer only</div>
        </div>
        <div className="metric">
          <div className="k">Remitted</div>
          <div className="v">{formatRs(view.totals.epfPaidRs + view.totals.etfPaidRs)}</div>
          <div className="k">{view.payments.length} payments</div>
        </div>
        <div
          className="metric"
          style={{ background: owed > 0 ? 'var(--bg-warning)' : 'var(--bg-success)' }}
        >
          <div className="k" style={{ color: owed > 0 ? 'var(--text-warning)' : 'var(--text-success)' }}>
            Still to remit
          </div>
          <div className="v" style={{ color: owed > 0 ? 'var(--text-warning)' : 'var(--text-success)' }}>
            {formatRs(owed)}
          </div>
          <div className="k" style={{ color: owed > 0 ? 'var(--text-warning)' : 'var(--text-success)' }}>
            {view.outstanding.length} member{view.outstanding.length === 1 ? '' : 's'} outstanding
          </div>
        </div>
      </div>

      {view.outstanding.length > 0 && (
        <>
          <div className="bar" style={{ marginTop: 14, alignItems: 'center' }}>
            <span className="sub" style={{ flex: 2, alignSelf: 'center' }}>
              Outstanding member by member — tick who is being paid
            </span>
            <button
              type="button"
              className="btn sm"
              style={{ minWidth: 0 }}
              onClick={() => setSkipped(new Set())}
            >
              Select all
            </button>
            <button
              type="button"
              className="btn sm"
              style={{ minWidth: 0 }}
              onClick={() => setSkipped(new Set(view.outstanding.map((r) => r.worker_id)))}
            >
              Clear
            </button>
          </div>

          <div className="scroll">
            <table className="tt">
              <thead>
                <tr>
                  <th>Pay</th>
                  <th>Member</th>
                  <th>EPF no</th>
                  <th className="num">Base salary</th>
                  <th className="num">EPF</th>
                  <th className="num">ETF</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {view.outstanding.map((row) => {
                  const on = !skipped.has(row.worker_id);
                  const epf = row.epf_employee_rs + row.epf_employer_rs;
                  return (
                    <tr key={row.worker_id} className={on ? undefined : 'dim'}>
                      <td>
                        <button
                          type="button"
                          className={`btn sm${on ? ' pri' : ''}`}
                          style={{ minWidth: 58 }}
                          onClick={() =>
                            setSkipped((current) => {
                              const next = new Set(current);
                              if (next.has(row.worker_id)) next.delete(row.worker_id);
                              else next.add(row.worker_id);
                              return next;
                            })
                          }
                        >
                          {on ? 'Pay' : 'Skip'}
                        </button>
                      </td>
                      <td className="nm">
                        {row.worker_name}
                        {row.runs > 1 && <div className="why">{row.runs} pay runs</div>}
                      </td>
                      <td>{row.epf_no ?? '—'}</td>
                      <td className="num">{formatRs(row.base_rs)}</td>
                      <td className="num">{formatRs(epf)}</td>
                      <td className="num">{formatRs(row.etf_rs)}</td>
                      <td className="num nm">{formatRs(epf + row.etf_rs)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={4}>
                    {picked.length} of {view.outstanding.length} selected
                  </th>
                  <th className="num">{formatRs(pickedTotals.epf)}</th>
                  <th className="num">{formatRs(pickedTotals.etf)}</th>
                  <th className="num">{formatRs(pickedTotals.epf + pickedTotals.etf)}</th>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Month, date and reference. No amount field, by design. */}
          <div className="sub" style={{ marginTop: 14 }}>Record the payment to the fund</div>
          <div className="bar" style={{ marginTop: 6 }}>
            <div style={{ flex: 1 }}>
              <div className="sub">For the month</div>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <div className="sub">Date paid</div>
              <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
            </div>
          </div>
          <div className="bar" style={{ marginTop: 8 }}>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Receipt or reference number"
              style={{ flex: 2 }}
            />
            <button
              type="button"
              className="btn pri"
              disabled={busy || picked.length === 0}
              onClick={() => void record()}
            >
              Record payment for {picked.length} member{picked.length === 1 ? '' : 's'}
            </button>
          </div>
          <div className="sub" style={{ marginTop: 6 }}>
            Only the members ticked above are recorded. The rest stay outstanding for a later
            payment.
          </div>
        </>
      )}

      {view.payments.length > 0 && (
        <>
          <div className="sub" style={{ marginTop: 14 }}>Payments made</div>
          <div className="scroll">
            <table className="tt">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Paid on</th>
                  <th>Reference</th>
                  <th className="num">Members</th>
                  <th className="num">EPF</th>
                  <th className="num">ETF</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {view.payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="nm">{payment.period_month}</td>
                    <td>{formatDate(payment.paid_on)}</td>
                    <td>{payment.reference ?? '—'}</td>
                    <td className="num">{payment.member_count}</td>
                    <td className="num">{formatRs(payment.epf_rs)}</td>
                    <td className="num">{formatRs(payment.etf_rs)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn sm"
                        style={{ minWidth: 0 }}
                        onClick={() => void printSchedule(payment.id)}
                      >
                        Schedule
                      </button>{' '}
                      {voiding === payment.id ? (
                        <>
                          <button
                            type="button"
                            className="btn sm dang"
                            style={{ minWidth: 0 }}
                            onClick={() =>
                              void reportsApi
                                .voidPayment(payment.id)
                                .then(load)
                                .finally(() => setVoiding(null))
                            }
                          >
                            Yes, cancel it
                          </button>{' '}
                          <button
                            type="button"
                            className="btn sm"
                            style={{ minWidth: 0 }}
                            onClick={() => setVoiding(null)}
                          >
                            Keep
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn sm dang"
                          style={{ minWidth: 0 }}
                          onClick={() => setVoiding(payment.id)}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="bar" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="btn"
          onClick={() =>
            void reportsApi.fundStatement().then((statement) =>
              openPrint(
                'EPF and ETF statement',
                <FundStatement
                  statement={statement}
                  printedBy={userName}
                  printedAt={new Date()}
                />,
              ),
            )
          }
        >
          Print EPF/ETF statement
        </button>
      </div>
    </div>
  );
}
